import './styles/App.scss';
import i18next from 'i18next';
import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { ConfigProvider } from 'antd';
import { AppLayout } from './components/Layout';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { Provider } from 'react-redux';
import { libraryActions } from './store/slices/library';
import { PersistGate } from 'redux-persist/integration/react';
import { persistor, store, useAppSelector } from './store/store';
import { SearchPage } from './pages/Search';

const Home = lazy(() => import('./pages/Home'));
const Page404 = lazy(() => import('./pages/404'));
const Profile = lazy(() => import('./pages/Profile'));
const PlaylistView = lazy(() => import('./pages/Playlist'));

/* ------------ ErrorBoundary: shows error text instead of blank page ------------ */
function ErrorBoundary({ children }: { children: React.ReactNode }) {
  const [err, setErr] = React.useState<Error | null>(null);

  useEffect(() => {
    const onError = (e: ErrorEvent) => setErr(e.error ?? new Error(String(e.message)));
    const onRejection = (e: PromiseRejectionEvent) =>
      setErr(e.reason instanceof Error ? e.reason : new Error(String(e.reason)));
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  if (err) {
    return (
      <div style={{ padding: 16, color: '#fff' }}>
        <h3>Oops, something crashed</h3>
        <pre style={{ whiteSpace: 'pre-wrap', color: '#ffb4b4' }}>
          {err.message}
          {'\n'}
          {err.stack}
        </pre>
      </div>
    );
  }
  return <>{children}</>;
}

const RootComponent = () => {
  const container = useRef<HTMLDivElement | null>(null);
  const language = useAppSelector((s) => s.language.language);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < 950) store.dispatch(libraryActions.collapseLibrary());
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
    i18next.changeLanguage(language);
  }, [language]);

  return (
    <Router>
      <AppLayout>
        <div className="Main-section" ref={container}>
          <div style={{ minHeight: 'calc(100vh - 230px)', width: '100%' }}>
            <ErrorBoundary>
              <Suspense fallback={<div style={{ padding: 16, color: '#bbb' }}>Loading…</div>}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/search/:search" element={<SearchPage />} />
                  <Route path="/playlist/:playlistId" element={<PlaylistView container={container} />} />
                  <Route path="*" element={<Page404 />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </div>
        </div>
      </AppLayout>
    </Router>
  );
};

function App() {
  return (
    <ConfigProvider theme={{ token: { fontFamily: 'SpotifyMixUI' } }}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <RootComponent />
        </PersistGate>
      </Provider>
    </ConfigProvider>
  );
}

export default App;
